import { useEffect, useRef, useState } from "react";
import { sendWhatsAppOtp, verifyWhatsAppOtp } from "@/services/auth";
import { cpfDigits, formatCpf, isValidCpf } from "@/utils/cpf";
import { getDeviceFingerprint } from "@/utils/device-fingerprint";

type Step = "phone" | "code" | "cpf";

interface Props {
  onError: (message: string) => void;
  onClearMessage: () => void;
}

const NO_ACCOUNT_MESSAGE =
  "Não encontramos uma conta com este número. Use o mesmo número cadastrado no app da Vammo.";

// The CPF sent did not match the account holding this number: ms-login treats
// it as a recycled phone and unlinks the number — there is nothing more the
// user can self-serve here.
const CPF_MISMATCH_MESSAGE =
  "O CPF informado não corresponde ao titular deste número. Fale com o suporte para reativar seu acesso.";

type CountryCode = "BR" | "CO";

interface Country {
  code: CountryCode;
  dialCode: string;
  flag: string;
  name: string;
  // National number length, excluding country code (E.164 total = dialCode + this)
  nationalDigits: number;
  placeholder: string;
}

const COUNTRIES: Country[] = [
  {
    code: "BR",
    dialCode: "+55",
    flag: "🇧🇷",
    name: "Brasil",
    nationalDigits: 11,
    placeholder: "11999999999",
  },
  {
    code: "CO",
    dialCode: "+57",
    flag: "🇨🇴",
    name: "Colombia",
    nationalDigits: 10,
    placeholder: "3001234567",
  },
];

const DEFAULT_COUNTRY: CountryCode = "BR";

export const WhatsAppLogin: React.FC<Props> = ({ onError, onClearMessage }) => {
  const [step, setStep] = useState<Step>("phone");
  const [countryCode, setCountryCode] = useState<CountryCode>(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [cpf, setCpf] = useState(""); // digits only — rendered through formatCpf
  const [verificationToken, setVerificationToken] = useState("");
  const [expiresIn, setExpiresIn] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const codeInputRef = useRef<HTMLInputElement>(null);
  const cpfInputRef = useRef<HTMLInputElement>(null);

  const country =
    COUNTRIES.find((c) => c.code === countryCode) ?? COUNTRIES[0];
  const phoneDigits = phone.replace(/\D/g, "");
  const fullPhone = `${country.dialCode}${phoneDigits}`;

  useEffect(() => {
    if (step !== "code" || secondsLeft <= 0) return;
    const id = window.setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [step, secondsLeft]);

  useEffect(() => {
    if (step === "code") {
      codeInputRef.current?.focus();
    }
    if (step === "cpf") {
      cpfInputRef.current?.focus();
    }
  }, [step]);

  const handleSend = async (event?: React.FormEvent): Promise<void> => {
    event?.preventDefault();
    onClearMessage();

    if (phoneDigits.length !== country.nationalDigits) {
      onError(
        `Informe um número válido de ${country.name} (${country.nationalDigits} dígitos, sem o código do país).`
      );
      return;
    }

    setIsLoading(true);
    try {
      const result = await sendWhatsAppOtp(fullPhone, getDeviceFingerprint());
      setVerificationToken(result.verification_token);
      setExpiresIn(result.expires_in);
      setSecondsLeft(result.expires_in);
      setStep("code");
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : "Falha ao enviar o código. Tente novamente."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (event?: React.FormEvent): Promise<void> => {
    event?.preventDefault();
    onClearMessage();

    if (code.length !== 6) {
      onError("O código deve ter 6 dígitos.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await verifyWhatsAppOtp(
        verificationToken,
        code,
        getDeviceFingerprint()
      );

      if (result.status === "dni_required") {
        // Account exists but never logged in through phone auth (or not for a
        // long time) — recycled-number protection. The 428 does NOT consume
        // the challenge: the same token + code are re-verified with the CPF.
        setStep("cpf");
        setIsLoading(false);
        return;
      }
      if (result.status === "signup_required") {
        onError(NO_ACCOUNT_MESSAGE);
        setIsLoading(false);
        return;
      }
      // status === "logged_in" — redirect already triggered inside verifyWhatsAppOtp
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : "Falha ao verificar o código. Tente novamente."
      );
      setIsLoading(false);
    }
  };

  const handleConfirmCpf = async (event?: React.FormEvent): Promise<void> => {
    event?.preventDefault();
    onClearMessage();

    if (!isValidCpf(cpf)) {
      onError("CPF inválido. Confira os 11 dígitos.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await verifyWhatsAppOtp(
        verificationToken,
        code,
        getDeviceFingerprint(),
        cpfDigits(cpf)
      );

      if (result.status === "signup_required") {
        // CPF did not match: the backend unlinked the number. Unlike the app
        // (which opens signup), the web audience came to log into an EXISTING
        // account — surface support instead of a registration flow.
        onError(CPF_MISMATCH_MESSAGE);
        setStep("phone");
        setCode("");
        setCpf("");
        setVerificationToken("");
        setSecondsLeft(0);
        setIsLoading(false);
        return;
      }
      // status === "logged_in" — redirect already triggered inside verifyWhatsAppOtp
      // (dni_required cannot repeat once a dni is sent — the challenge is consumed.)
    } catch (error) {
      // The CPF attempt consumes the challenge: if this failed (network,
      // expired code), the same token can be dead — offer a fresh code.
      onError(
        error instanceof Error
          ? error.message
          : "Falha ao confirmar o CPF. Peça um novo código e tente novamente."
      );
      setIsLoading(false);
    }
  };

  // Also the escape hatch from the CPF step: a failed CPF attempt consumes
  // the challenge, so recovering means a fresh code. handleSend moves to the
  // code step only on success — a failed resend keeps the current screen.
  const handleResend = async (): Promise<void> => {
    setCode("");
    await handleSend();
  };

  const handleBack = (): void => {
    setStep("phone");
    setCode("");
    setCpf("");
    setVerificationToken("");
    setSecondsLeft(0);
    onClearMessage();
  };

  if (step === "phone") {
    return (
      <form onSubmit={handleSend} className="flex flex-col gap-3">
        <label
          htmlFor="wa-phone"
          className="text-white/70 text-xs uppercase tracking-[0.15em] font-medium"
        >
          WhatsApp
        </label>
        <div className="flex gap-2">
          <label htmlFor="wa-country" className="sr-only">
            País
          </label>
          <div className="relative">
            <select
              id="wa-country"
              value={countryCode}
              onChange={(e) => {
                setCountryCode(e.target.value as CountryCode);
                setPhone("");
              }}
              disabled={isLoading}
              className="appearance-none pl-3 pr-8 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[rgba(46,194,255,0.6)] focus:bg-white/8 transition-colors cursor-pointer disabled:cursor-not-allowed"
              aria-label="Código do país"
            >
              {COUNTRIES.map((c) => (
                <option
                  key={c.code}
                  value={c.code}
                  className="bg-[#0a0a0a] text-white"
                >
                  {c.flag} {c.dialCode}
                </option>
              ))}
            </select>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-white/50 text-xs"
            >
              ▾
            </span>
          </div>
          <input
            id="wa-phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            placeholder={country.placeholder}
            value={phone}
            onChange={(e) =>
              setPhone(
                e.target.value
                  .replace(/\D/g, "")
                  .slice(0, country.nationalDigits)
              )
            }
            disabled={isLoading}
            className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-[rgba(46,194,255,0.6)] focus:bg-white/8 transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={isLoading}
          className="mt-1 px-4 py-3 rounded-xl bg-[rgba(46,194,255,0.9)] hover:bg-[rgba(46,194,255,1)] text-black font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? "Enviando…" : "Enviar código por WhatsApp"}
        </button>
      </form>
    );
  }

  if (step === "cpf") {
    return (
      <form onSubmit={handleConfirmCpf} className="flex flex-col gap-3">
        <label
          htmlFor="wa-cpf"
          className="text-white/70 text-xs uppercase tracking-[0.15em] font-medium"
        >
          Para sua segurança, confirme seu CPF
        </label>
        <p className="text-white/50 text-xs leading-relaxed">
          Sua conta não faz login há algum tempo. Confirme o CPF do titular
          para continuar.
        </p>
        <input
          id="wa-cpf"
          ref={cpfInputRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="000.000.000-00"
          value={formatCpf(cpf)}
          onChange={(e) => setCpf(cpfDigits(e.target.value))}
          disabled={isLoading}
          className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-[rgba(46,194,255,0.6)] focus:bg-white/8 transition-colors tracking-[0.15em] text-center text-lg"
        />
        <p className="flex gap-2 items-start text-[0.7rem] leading-relaxed text-amber-200/90 bg-amber-400/10 border border-amber-400/25 rounded-lg px-3 py-2">
          <span aria-hidden="true">⚠</span>
          <span>
            Confira o CPF antes de enviar — ele precisa ser o do titular desta
            conta.
          </span>
        </p>
        <button
          type="submit"
          disabled={isLoading || !isValidCpf(cpf)}
          className="mt-1 px-4 py-3 rounded-xl bg-[rgba(46,194,255,0.9)] hover:bg-[rgba(46,194,255,1)] text-black font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? "Verificando…" : "Confirmar CPF"}
        </button>

        <div className="flex justify-between text-xs text-white/50 mt-1">
          <button
            type="button"
            onClick={handleBack}
            disabled={isLoading}
            className="hover:text-white/80 transition-colors disabled:opacity-50"
          >
            ← Trocar número
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={isLoading}
            className="hover:text-white/80 transition-colors disabled:opacity-50"
          >
            Pedir novo código
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleVerify} className="flex flex-col gap-3">
      <label
        htmlFor="wa-code"
        className="text-white/70 text-xs uppercase tracking-[0.15em] font-medium"
      >
        Código enviado para {country.flag} {fullPhone}
      </label>
      <input
        id="wa-code"
        ref={codeInputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d{6}"
        maxLength={6}
        placeholder="000000"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        disabled={isLoading}
        className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-[rgba(46,194,255,0.6)] focus:bg-white/8 transition-colors tracking-[0.4em] text-center text-lg"
      />
      <button
        type="submit"
        disabled={isLoading || code.length !== 6}
        className="mt-1 px-4 py-3 rounded-xl bg-[rgba(46,194,255,0.9)] hover:bg-[rgba(46,194,255,1)] text-black font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isLoading ? "Verificando…" : "Entrar"}
      </button>

      <div className="flex justify-between text-xs text-white/50 mt-1">
        <button
          type="button"
          onClick={handleBack}
          disabled={isLoading}
          className="hover:text-white/80 transition-colors disabled:opacity-50"
        >
          ← Trocar número
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={isLoading || secondsLeft > 0}
          className="hover:text-white/80 transition-colors disabled:opacity-50"
        >
          {secondsLeft > 0
            ? `Reenviar em ${secondsLeft}s`
            : "Reenviar código"}
        </button>
      </div>
      {expiresIn > 0 && secondsLeft > 0 && (
        <p className="text-[0.7rem] text-white/35 text-center">
          O código expira em {secondsLeft}s.
        </p>
      )}
    </form>
  );
};
